/**
 * FAQDialog Component
 * Dialog for creating and editing FAQs
 *
 * Enhanced UX with visual hierarchy, markdown preview, and metadata display
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  Paper,
  Typography,
  Divider,
  Stack,
  Chip,
  Avatar,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

import { BaseDialog } from '../ui/BaseDialog';
import { FAQDocumentResponse, FAQDocumentCreate, DocumentVisibility } from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { VisibilitySection } from '../ui/VisibilitySection';
import { SharingSection } from '../ui/SharingSection';
import { usePreferredVisibility } from '../../stores/sharingStore';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { getUserDisplayName, formatDateTime } from '../../utils/formatting/formatters';

interface FAQDialogProps {
  open: boolean;
  onClose: () => void;
  editingFAQ: FAQDocumentResponse | null;
  onSubmit: (faqData: FAQDocumentCreate, isEdit: boolean) => Promise<void>;
}

interface FAQFormData {
  question: string;
  answer: string;
  language: string;
  visibility: string;
  shared_with_users: string;
  shared_with_groups: string;
  category_tags: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel helper component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`faq-tabpanel-${index}`}
      aria-labelledby={`faq-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const FAQDialog: React.FC<FAQDialogProps> = ({
  open,
  onClose,
  editingFAQ,
  onSubmit
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { preferredVisibility, setPreferredVisibility } = usePreferredVisibility();

  // Tab state for answer editing vs preview
  const [answerTab, setAnswerTab] = useState(0);

  // Form state
  const [faqForm, setFaqForm] = useState<FAQFormData>({
    question: editingFAQ?.question || '',
    answer: editingFAQ?.answer || '',
    language: editingFAQ?.language || 'de',
    visibility: editingFAQ?.visibility || preferredVisibility,
    shared_with_users: editingFAQ?.shared_with_users?.join(', ') || '',
    shared_with_groups: editingFAQ?.shared_with_groups?.join(', ') || '',
    category_tags: editingFAQ?.category_tags?.join(', ') || '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      if (editingFAQ) {
        setFaqForm({
          question: editingFAQ.question || '',
          answer: editingFAQ.answer || '',
          language: editingFAQ.language || 'de',
          visibility: editingFAQ.visibility || 'private',
          shared_with_users: editingFAQ.shared_with_users?.join(', ') || '',
          shared_with_groups: editingFAQ.shared_with_groups?.join(', ') || '',
          category_tags: editingFAQ.category_tags?.join(', ') || '',
        });
      } else {
        setFaqForm({
          question: '',
          answer: '',
          language: 'de',
          visibility: preferredVisibility,
          shared_with_users: '',
          shared_with_groups: '',
          category_tags: '',
        });
      }
      setSubmitError(null);
      setSubmitting(false);
      setAnswerTab(0); // Reset to edit tab
    }
  }, [open, editingFAQ]);

  // Form validation
  const isFormValid = useMemo(() => {
    return (
      faqForm.question.trim().length >= 10 &&
      faqForm.question.trim().length <= 500 &&
      faqForm.answer.trim().length >= 20
    );
  }, [faqForm]);

  // Handle form field changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFaqForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle select changes
  const handleSelectChange = (name: string) => (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFaqForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Save preferred visibility when user changes it
    if (name === 'visibility') {
      setPreferredVisibility(value);
    }
  };



  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare FAQ data
      // Map to correct FAQDocumentCreate interface
      const faqData: FAQDocumentCreate = {
        title: faqForm.question.trim(),
        content: faqForm.answer.trim(),
        question: faqForm.question.trim(),
        answer: faqForm.answer.trim(),
        visibility: faqForm.visibility as any,
        shared_with_users: faqForm.shared_with_users ?
          faqForm.shared_with_users.split(',').map(u => u.trim()).filter(Boolean) : [],
        shared_with_groups: faqForm.shared_with_groups ?
          faqForm.shared_with_groups.split(',').map(g => g.trim()).filter(Boolean) : [],
        category_tags: faqForm.category_tags ?
          faqForm.category_tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        source_type: 'faq'
      };

      log.user.action('faq_form_submit', 'FAQDialog', {
        isEdit: Boolean(editingFAQ),
        question: `${faqData.title?.substring(0, 50)  }...` || 'No title'
      });

      await onSubmit(faqData, Boolean(editingFAQ));
      onClose();
    } catch (error: any) {
      log.error('FAQ form submission failed', { error: error.message });
      setSubmitError(error.message || t('faq.error.saveFailed', 'Failed to save FAQ'));
    } finally {
      setSubmitting(false);
    }
  };

  // Markdown components for preview (same as detail dialog)
  const markdownComponents = {
    p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.8 }} {...props} />
    ),
    h1: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h4" component="h1" sx={{ mt: 3, mb: 2, fontWeight: 700 }} {...props} />
    ),
    h2: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h5" component="h2" sx={{ mt: 2.5, mb: 1.5, fontWeight: 600 }} {...props} />
    ),
    h3: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 600 }} {...props} />
    ),
    ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <Box component="ul" sx={{ pl: 3, my: 1.5, '& li': { mb: 0.75 } }} {...props} />
    ),
    ol: ({ ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
      <Box component="ol" sx={{ pl: 3, my: 1.5, '& li': { mb: 0.75 } }} {...props} />
    ),
    li: ({ ...props }: React.ComponentProps<'li'>) => (
      <Typography component="li" variant="body1" sx={{ lineHeight: 1.8 }} {...props} />
    ),
    a: ({ href, children, ...props }: React.ComponentProps<'a'>) => (
      <Box
        component="a"
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          textDecorationColor: alpha(theme.palette.primary.main, 0.4),
          '&:hover': { textDecorationColor: theme.palette.primary.main, color: 'primary.dark' },
        }}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </Box>
    ),
    code: ({ ...props }: React.ComponentProps<'code'>) => (
      <Box
        component="code"
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: 'primary.dark',
          padding: '3px 8px',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.875em',
          fontWeight: 500,
        }}
        {...props}
      />
    ),
    blockquote: ({ ...props }: React.ComponentProps<'blockquote'>) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          pl: 2,
          py: 0.5,
          my: 2,
          ml: 0,
          fontStyle: 'italic',
          color: 'text.secondary',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        }}
        {...props}
      />
    ),
  };

  // Calculate stats for editing FAQ
  const totalVotes = editingFAQ ? (editingFAQ.helpful_count || 0) + (editingFAQ.not_helpful_count || 0) : 0;
  const helpfulPercentage = totalVotes > 0
    ? Math.round(((editingFAQ?.helpful_count || 0) / totalVotes) * 100)
    : 0;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={editingFAQ ? t('faq.actions.edit') : t('faq.createButton')}
      subtitle={editingFAQ ? t('faq.edit.subtitle', 'Update your FAQ content and settings') : t('faq.create.subtitle', 'Share knowledge with your organization')}
      maxWidth="md"
      fullWidth
      loading={submitting}
      error={submitError}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid || submitting}
          >
            {editingFAQ ? t('common.save') :
              (faqForm.visibility !== 'private' ?
                t('faq.actions.create_and_submit', 'Create & Submit for Approval') :
                t('faq.createButton')
              )
            }
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        {/* Metadata Section - Only for editing */}
        {editingFAQ && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              borderColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Stack spacing={2}>
              {/* Status and Stats Row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <ApprovalStatusBadge status={editingFAQ.approval_status || 'draft'} />

                {editingFAQ.language && (
                  <Chip
                    label={editingFAQ.language === 'de' ? 'Deutsch' : 'English'}
                    size="small"
                    variant="outlined"
                  />
                )}

                {totalVotes > 0 && (
                  <Chip
                    icon={<TrendingUpIcon />}
                    label={`${helpfulPercentage}% ${t('faq.detail.helpful', 'Helpful')} (${totalVotes} ${totalVotes === 1 ? 'vote' : 'votes'})`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Creator Info */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('faq.detail.createdBy', 'Created by')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {getUserDisplayName(editingFAQ, t('common.unknown_user', 'Unknown User'))}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('faq.detail.lastUpdated', 'Last updated')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDateTime(editingFAQ.updated_at || editingFAQ.created_at || '', i18n.language)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Rejection reason if applicable */}
              {editingFAQ.approval_status === 'rejected' && editingFAQ.rejection_reason && (
                <Alert severity="warning" icon={<InfoIcon />}>
                  <Typography variant="caption" fontWeight={600}>
                    {t('faq.actions.rejection_reason', 'Rejection Reason')}:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {editingFAQ.rejection_reason}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Paper>
        )}

        {/* Question Section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary">
                {t('faq.fields.question')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({t('common.required', 'Required')})
              </Typography>
            </Box>

            <TextField
              name="question"
              placeholder={t('faq.edit.questionPlaceholder', 'Enter a clear, concise question...')}
              value={faqForm.question}
              onChange={handleFormChange}
              fullWidth
              required
              multiline
              rows={2}
              helperText={
                faqForm.question.length < 10 && faqForm.question.length > 0
                  ? t('faq.validation.questionMin')
                  : `${faqForm.question.length}/500 ${t('common.characters', 'characters')}`
              }
              error={faqForm.question.length > 500 || (faqForm.question.length > 0 && faqForm.question.length < 10)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Stack>
        </Paper>

        {/* Answer Section with Tabs */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary">
                  {t('faq.fields.answer')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({t('common.required', 'Required')})
                </Typography>
              </Box>

              <Tabs
                value={answerTab}
                onChange={(_, newValue) => setAnswerTab(newValue)}
                sx={{ minHeight: 36 }}
              >
                <Tab
                  icon={<EditIcon fontSize="small" />}
                  iconPosition="start"
                  label={t('faq.edit.editTab', 'Edit')}
                  sx={{ minHeight: 36, py: 0.5 }}
                />
                <Tab
                  icon={<PreviewIcon fontSize="small" />}
                  iconPosition="start"
                  label={t('faq.edit.previewTab', 'Preview')}
                  sx={{ minHeight: 36, py: 0.5 }}
                />
              </Tabs>
            </Box>

            <TabPanel value={answerTab} index={0}>
              <TextField
                name="answer"
                placeholder={t('faq.edit.answerPlaceholder', 'Provide a detailed answer using Markdown formatting...')}
                value={faqForm.answer}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={8}
                helperText={
                  faqForm.answer.length < 20 && faqForm.answer.length > 0
                    ? t('faq.validation.answerMin')
                    : `${faqForm.answer.length} ${t('common.characters', 'characters')} • ${t('faq.edit.markdownSupported', 'Markdown supported')}`
                }
                error={faqForm.answer.length > 0 && faqForm.answer.length < 20}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                    fontFamily: 'monospace',
                  },
                }}
              />
            </TabPanel>

            <TabPanel value={answerTab} index={1}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  minHeight: 200,
                  backgroundColor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {faqForm.answer.trim() ? (
                  <Box
                    sx={{
                      '& > *:first-of-type': { mt: 0 },
                      '& > *:last-child': { mb: 0 },
                    }}
                  >
                    <ReactMarkdown components={markdownComponents}>
                      {faqForm.answer}
                    </ReactMarkdown>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t('faq.edit.previewEmpty', 'Answer preview will appear here...')}
                  </Typography>
                )}
              </Paper>
            </TabPanel>
          </Stack>
        </Paper>

        {/* Categories Section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              {t('faq.edit.categories', 'Categories & Language')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="category_tags"
                  label={t('faq.edit.categoryTags', 'Category Tags')}
                  value={faqForm.category_tags}
                  onChange={handleFormChange}
                  fullWidth
                  placeholder={t('faq.edit.categoryTagsPlaceholder', 'e.g., SAP, AI, Integration')}
                  helperText={t('faq.edit.categoryTagsHelp', 'Comma-separated tags for easier discovery')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('common.language', 'Language')}</InputLabel>
                  <Select
                    name="language"
                    value={faqForm.language}
                    onChange={handleSelectChange('language')}
                    sx={{
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <MenuItem value="de">Deutsch</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Preview category chips */}
            {faqForm.category_tags && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 1 }}>
                {faqForm.category_tags.split(',').map((tag, index) => {
                  const trimmedTag = tag.trim();
                  return trimmedTag ? (
                    <Chip
                      key={index}
                      label={trimmedTag}
                      size="small"
                      color="secondary"
                      variant="filled"
                    />
                  ) : null;
                })}
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Visibility & Sharing Section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              {t('faq.permissions.title', 'Permissions & Privacy Settings')}
            </Typography>

            <VisibilitySection
              visibility={faqForm.visibility}
              onChange={(visibility) => {
                setFaqForm(prev => ({
                  ...prev,
                  visibility
                }));
                setPreferredVisibility(visibility);
              }}
            />

            <SharingSection
              userIds={faqForm.shared_with_users}
              groupIds={faqForm.shared_with_groups}
              onChange={(userIds, groupIds) => {
                setFaqForm(prev => ({
                  ...prev,
                  shared_with_users: userIds,
                  shared_with_groups: groupIds
                }));
              }}
              title=""
              helperText={t('faq.sharing.recipientsHelp', 'Choose users and groups to share this FAQ with')}
            />
          </Stack>
        </Paper>
      </Stack>
    </BaseDialog>
  );
};